import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  getVersions,
  getVersion,
  compareVersions,
  restoreVersion,
  deleteVersion,
  setVersionProtected,
} from "../api";
import { C, TABLE, LAYOUT } from "../constants";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import { FancySelect } from "../components/FormControls";
import { useAppNotify } from "../components/AppNotify";

/** Match PlanTable schedule header */
const TABLE_HEAD = {
  background: TABLE.headBg,
  color: TABLE.headText,
  fontWeight: 700,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  padding: "10px 14px",
  textAlign: "left",
  borderRight: `1px solid ${TABLE.headBorder}`,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

const TD = {
  padding: "12px 14px",
  verticalAlign: "middle",
  fontFamily: "inherit",
};

/** Wrap at spaces only — avoid one-letter-per-line mid-word breaks */
const wrapCell = {
  whiteSpace: "normal",
  wordBreak: "keep-all",
  overflowWrap: "break-word",
  hyphens: "none",
  lineHeight: 1.35,
};

const TYPE_STYLE = {
  moved: { label: "Moved", bg: C.primarySoft, color: C.primaryDark },
  modified: { label: "Updated", bg: C.warningBg, color: C.warning },
  added: { label: "Added", bg: C.successBg, color: C.success },
  removed: { label: "Removed", bg: C.errorBg, color: C.error },
};

function friendlyChangeType(changeType = "") {
  const ct = String(changeType || "").trim();
  if (!ct) return "Update";
  if (/^initial plan$/i.test(ct)) return "Generated";
  if (/^manual edit$/i.test(ct)) return "Edited";
  if (/^day regenerat/i.test(ct)) return "Day regenerated";
  if (/^regenerat/i.test(ct)) return "Regenerated";
  if (/restor|rollback/i.test(ct)) return "Restored";
  return ct;
}

function byEmail(v) {
  const email = String(v?.changedByEmail || "").trim();
  return email.includes("@") ? email : "";
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(iso);
  }
}

const actionBtnBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 5,
  height: 30,
  padding: "0 12px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 800,
  fontFamily: "inherit",
  letterSpacing: "-0.01em",
  cursor: "pointer",
  border: "1.5px solid transparent",
  whiteSpace: "nowrap",
  lineHeight: 1,
};

const iconBtnBase = {
  ...actionBtnBase,
  width: 30,
  padding: 0,
};

function VersionActions({
  versionNum,
  isLatest,
  isProtected,
  restoring,
  viewing,
  onView,
  onRollback,
  onProtect,
  onDelete,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuBox, setMenuBox] = useState(null);
  const wrapRef = useRef(null);
  const moreRef = useRef(null);

  const placeMenu = () => {
    const el = moreRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 232;
    const left = Math.min(
      Math.max(8, r.right - width),
      window.innerWidth - width - 8
    );
    setMenuBox({
      top: r.bottom + 6,
      left,
      width,
    });
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    placeMenu();
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const menuEl = document.getElementById(`vh-actions-${versionNum}`);
        if (menuEl && menuEl.contains(e.target)) return;
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onReposition = () => placeMenu();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [menuOpen, versionNum]);

  const menuItem = (opts) => (
    <button
      key={opts.key}
      type="button"
      disabled={opts.disabled}
      title={opts.title}
      onClick={() => {
        if (opts.disabled) return;
        setMenuOpen(false);
        opts.onClick?.();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        textAlign: "left",
        padding: "10px 12px",
        border: "none",
        background: opts.danger ? C.errorBg : "transparent",
        color: opts.disabled ? C.faint : opts.danger ? C.error : C.text,
        fontSize: 13,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor: opts.disabled ? "not-allowed" : "pointer",
        borderRadius: 8,
        opacity: opts.disabled ? 0.55 : 1,
      }}
      onMouseEnter={(e) => {
        if (!opts.disabled && !opts.danger) e.currentTarget.style.background = C.surfaceAlt;
      }}
      onMouseLeave={(e) => {
        if (!opts.danger) e.currentTarget.style.background = "transparent";
      }}
    >
      <i className={`ti ${opts.icon}`} style={{ fontSize: 15, width: 18, textAlign: "center" }} />
      <span style={{ flex: 1 }}>{opts.label}</span>
      {opts.hint ? (
        <span style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>{opts.hint}</span>
      ) : null}
    </button>
  );

  return (
    <div
      ref={wrapRef}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        position: "relative",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        title="View this schedule"
        onClick={() => onView(versionNum)}
        style={{
          ...iconBtnBase,
          background: viewing ? C.primary : C.primarySoft,
          color: viewing ? "#fff" : C.primaryDark,
          borderColor: `${C.primary}55`,
        }}
      >
        <i className={`ti ${viewing ? "ti-loader-2" : "ti-eye"}`} style={{ fontSize: 15 }} />
      </button>

      {isProtected ? (
        <span
          title="Faculty-protected — auto regen / leave won’t overwrite locked slots"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: 7,
            background: C.warningBg,
            color: C.warning,
            border: `1px solid ${C.warning}44`,
            flexShrink: 0,
          }}
        >
          <i className="ti ti-lock" style={{ fontSize: 13 }} />
        </span>
      ) : null}

      <button
        ref={moreRef}
        type="button"
        title="More actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
        style={{
          ...iconBtnBase,
          background: menuOpen ? C.ink : C.surface,
          color: menuOpen ? "#fff" : C.text,
          borderColor: menuOpen ? C.ink : C.borderStrong,
        }}
      >
        <i className="ti ti-dots-vertical" style={{ fontSize: 16 }} />
      </button>

      {menuOpen && menuBox
        ? createPortal(
            <div
              id={`vh-actions-${versionNum}`}
              role="menu"
              style={{
                position: "fixed",
                top: menuBox.top,
                left: menuBox.left,
                width: menuBox.width,
                zIndex: 1200,
                padding: 6,
                borderRadius: 12,
                background: "#fff",
                border: `1.5px solid ${C.border}`,
                boxShadow: "0 12px 32px rgba(10,14,23,0.16)",
              }}
            >
              {menuItem({
                key: "rollback",
                icon: "ti-history",
                label: restoring ? "Rolling back…" : "Rollback",
                hint: isLatest ? "Latest" : null,
                title: isLatest
                  ? "Already the latest version"
                  : "Restore this schedule as the new latest",
                disabled: isLatest || restoring,
                onClick: () => onRollback(versionNum),
              })}
              {menuItem({
                key: "protect",
                icon: isProtected ? "ti-lock-open" : "ti-lock",
                label: isProtected ? "Remove protection" : "Protect version",
                title: isProtected
                  ? "Allow auto regen / leave to change this version’s slots"
                  : "Lock faculty changes so auto regen / leave won’t overwrite them",
                onClick: () => onProtect(versionNum, isProtected),
              })}
              <div style={{ height: 1, background: C.border, margin: "4px 6px" }} />
              {menuItem({
                key: "delete",
                icon: "ti-trash",
                label: "Delete version",
                title: "Permanently delete this version snapshot",
                danger: true,
                onClick: () => onDelete(versionNum),
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

/** Compact status chips for the Type column */
function TypeChips({ v }) {
  const kind = friendlyChangeType(v.changeType);
  const chips = [];
  if (v.isLatestForMember) {
    chips.push({ key: "latest", label: "Latest", bg: C.primarySoft, color: C.primaryDark });
  }
  if (kind && !(v.isLatestForMember && /^(Generated|Update)$/i.test(kind))) {
    chips.push({ key: "kind", label: kind, bg: C.surfaceAlt, color: C.text });
  } else if (!v.isLatestForMember) {
    chips.push({ key: "kind", label: kind || "Update", bg: C.surfaceAlt, color: C.text });
  }
  if (v.protected) {
    chips.push({ key: "prot", label: "Protected", bg: C.warningBg, color: C.warning });
  }
  if (!chips.length) {
    chips.push({ key: "upd", label: "Update", bg: C.surfaceAlt, color: C.text });
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }} title={chips.map((c) => c.label).join(" · ")}>
      {chips.map((c) => (
        <span
          key={c.key}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800,
            background: c.bg,
            color: c.color,
            whiteSpace: "nowrap",
            lineHeight: 1.2,
          }}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

/** Read-only schedule snapshot for one student · one version — week/day like Planner */
function summarizePlanRows(rows = []) {
  const dayIds = new Set();
  const weeks = new Set();
  for (const r of rows) {
    if (!Array.isArray(r) || !r[0]) continue;
    const day = String(r[0]);
    dayIds.add(day);
    const wm = day.match(/Week\s*(\d+)/i);
    if (wm) weeks.add(Number(wm[1]));
  }
  return {
    activityRows: rows.length,
    days: dayIds.size,
    weeks: weeks.size || (dayIds.size ? 1 : 0),
    weekList: [...weeks].sort((a, b) => a - b),
    dayList: [...dayIds],
  };
}

function buildWeekDayIndex(rows = []) {
  const byWeekDay = {};
  const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  let autoWeek = 1;
  let lastDayIdx = -1;
  for (const row of rows) {
    if (!Array.isArray(row) || !row[0]) continue;
    const raw = String(row[0]);
    const m = raw.match(/Week\s*(\d+)\s*[-–]\s*(\w+)/i);
    let week = m ? parseInt(m[1], 10) : null;
    let dayName = m ? m[2] : null;
    if (!dayName) {
      const m2 = raw.match(/\b(Monday|Tuesday|Wednesday|Thursday|Friday)\b/i);
      dayName = m2 ? m2[1] : "Monday";
    }
    // Normalize casing to planner day names
    const hit = DAY_ORDER.find((d) => d.toLowerCase() === String(dayName).toLowerCase());
    dayName = hit || dayName;
    if (week == null || !Number.isFinite(week)) {
      const dIdx = DAY_ORDER.indexOf(dayName);
      if (dIdx !== -1 && dIdx <= lastDayIdx) autoWeek += 1;
      lastDayIdx = dIdx !== -1 ? dIdx : lastDayIdx;
      week = autoWeek;
    }
    if (!byWeekDay[week]) byWeekDay[week] = {};
    if (!byWeekDay[week][dayName]) byWeekDay[week][dayName] = [];
    byWeekDay[week][dayName].push(row);
  }
  const weeks = Object.keys(byWeekDay)
    .map(Number)
    .sort((a, b) => a - b);
  return { byWeekDay, weeks, DAY_ORDER };
}

function VersionSchedulePreview({
  label,
  plan,
  loading,
  error,
  onClose,
  onOpenInPlanner,
}) {
  const rows = Array.isArray(plan?.rows) ? plan.rows : Array.isArray(plan) ? plan : [];
  const stats = summarizePlanRows(rows);
  const { byWeekDay, weeks, DAY_ORDER } = buildWeekDayIndex(rows);
  const [activeWeek, setActiveWeek] = React.useState(weeks[0] || 1);
  const [activeDay, setActiveDay] = React.useState("Monday");
  const [showAll, setShowAll] = React.useState(false);

  React.useEffect(() => {
    if (weeks.length && !weeks.includes(activeWeek)) {
      setActiveWeek(weeks[0]);
    }
  }, [weeks.join(","), activeWeek]);

  React.useEffect(() => {
    const daysIn = byWeekDay[activeWeek] || {};
    const available = DAY_ORDER.filter((d) => (daysIn[d] || []).length);
    if (available.length && !available.includes(activeDay)) {
      setActiveDay(available[0]);
    }
  }, [activeWeek, activeDay, byWeekDay, DAY_ORDER]);

  const dayRows = (byWeekDay[activeWeek] || {})[activeDay] || [];
  const daysWithContent = DAY_ORDER.filter((d) => ((byWeekDay[activeWeek] || {})[d] || []).length);

  return (
    <Card style={{ marginBottom: 14, padding: 0 }} sx={{ p: "0 !important", overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          padding: "12px 16px",
          borderBottom: `1px solid ${C.border}`,
          background: C.primarySoft,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 850, color: C.text }}>
            Viewing schedule — {label || "Version"}
          </div>
          <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginTop: 2 }}>
            Exact saved snapshot
            {stats.weeks
              ? ` · ${stats.weeks} week(s) · ${stats.days} day(s) · ${stats.activityRows} activity row(s)`
              : ""}
            {" · "}Browse Week/Day like Planner (not the whole calendar at once).
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {rows.length > 0 && (
            <Btn small variant="ghost" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "Week / day view" : "Show all rows"}
            </Btn>
          )}
          {typeof onOpenInPlanner === "function" && rows.length > 0 && (
            <Btn small onClick={onOpenInPlanner}>
              Open in Planner
            </Btn>
          )}
          <Btn small variant="ghost" onClick={onClose}>
            Close
          </Btn>
        </div>
      </div>
      {loading && (
        <p style={{ padding: 20, color: C.muted, fontSize: 13, fontWeight: 600 }}>Loading schedule…</p>
      )}
      {error && (
        <p style={{ padding: 20, color: C.error, fontSize: 13, fontWeight: 600 }}>{error}</p>
      )}
      {!loading && !error && rows.length === 0 && (
        <p style={{ padding: 20, color: C.muted, fontSize: 13, fontWeight: 600 }}>
          This version has no schedule rows.
        </p>
      )}
      {!loading && !error && rows.length > 0 && !showAll && (
        <>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              padding: "10px 14px",
              borderBottom: `1px solid ${C.border}`,
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 800, color: C.muted }}>Week</span>
            {weeks.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setActiveWeek(w)}
                style={{
                  ...actionBtnBase,
                  background: activeWeek === w ? C.primary : C.surface,
                  color: activeWeek === w ? "#fff" : C.text,
                  borderColor: activeWeek === w ? C.primary : C.borderStrong,
                }}
              >
                W{w}
              </button>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              padding: "10px 14px",
              borderBottom: `1px solid ${C.border}`,
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 800, color: C.muted }}>Day</span>
            {daysWithContent.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setActiveDay(d)}
                style={{
                  ...actionBtnBase,
                  background: activeDay === d ? C.primarySoft : C.surface,
                  color: activeDay === d ? C.primaryDark : C.text,
                  borderColor: activeDay === d ? `${C.primary}55` : C.borderStrong,
                }}
              >
                {d.slice(0, 3)}
              </button>
            ))}
            <span style={{ fontSize: 12, color: C.muted, fontWeight: 650, marginLeft: 4 }}>
              Week {activeWeek} · {activeDay} · {dayRows.length} slot(s)
            </span>
          </div>
          <div style={{ overflow: "auto", maxHeight: "min(60vh, 640px)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={{ ...TABLE_HEAD, position: "sticky", top: 0, zIndex: 1, width: 90 }}>Time</th>
                  <th style={{ ...TABLE_HEAD, position: "sticky", top: 0, zIndex: 1, width: 140 }}>Activity</th>
                  <th style={{ ...TABLE_HEAD, position: "sticky", top: 0, zIndex: 1, borderRight: "none" }}>
                    Content
                  </th>
                </tr>
              </thead>
              <tbody>
                {dayRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ padding: 16, color: C.muted, fontWeight: 600 }}>
                      No activities on this day in this version.
                    </td>
                  </tr>
                ) : (
                  dayRows.map((r, i) => (
                    <tr
                      key={`${r[1]}-${r[2]}-${i}`}
                      style={{
                        background: i % 2 ? C.surfaceAlt : C.surface,
                        borderBottom: `1px solid ${C.border}`,
                        verticalAlign: "top",
                      }}
                    >
                      <td style={{ padding: "10px 12px", fontWeight: 650, color: C.muted, whiteSpace: "nowrap" }}>
                        {String(r[1] || "")}
                      </td>
                      <td style={{ padding: "10px 12px", fontWeight: 800, color: C.primaryDark, ...wrapCell }}>
                        {String(r[2] || "")}
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          fontWeight: 550,
                          color: C.text,
                          whiteSpace: "pre-wrap",
                          wordBreak: "keep-all",
                          overflowWrap: "break-word",
                          lineHeight: 1.45,
                        }}
                      >
                        {String(r[3] ?? "")}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!loading && !error && rows.length > 0 && showAll && (
        <div style={{ overflow: "auto", maxHeight: "min(70vh, 720px)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 640 }}>
            <thead>
              <tr>
                <th style={{ ...TABLE_HEAD, position: "sticky", top: 0, zIndex: 1 }}>Day</th>
                <th style={{ ...TABLE_HEAD, position: "sticky", top: 0, zIndex: 1, width: 90 }}>Time</th>
                <th style={{ ...TABLE_HEAD, position: "sticky", top: 0, zIndex: 1, width: 140 }}>Activity</th>
                <th style={{ ...TABLE_HEAD, position: "sticky", top: 0, zIndex: 1, borderRight: "none" }}>
                  Content
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                if (!Array.isArray(r)) return null;
                return (
                  <tr
                    key={`${r[0]}-${r[1]}-${r[2]}-${i}`}
                    style={{
                      background: i % 2 ? C.surfaceAlt : C.surface,
                      borderBottom: `1px solid ${C.border}`,
                      verticalAlign: "top",
                    }}
                  >
                    <td style={{ padding: "10px 12px", fontWeight: 750, whiteSpace: "nowrap", color: C.text }}>
                      {String(r[0] || "")}
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 650, color: C.muted, whiteSpace: "nowrap" }}>
                      {String(r[1] || "")}
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: C.primaryDark, ...wrapCell }}>
                      {String(r[2] || "")}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        fontWeight: 550,
                        color: C.text,
                        whiteSpace: "pre-wrap",
                        wordBreak: "keep-all",
                        overflowWrap: "break-word",
                        lineHeight: 1.45,
                      }}
                    >
                      {String(r[3] ?? "")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function CompareView({ diff, fromLabel, toLabel, fromVersion, toVersion, onClose }) {
  if (!diff) return null;
  const from = fromLabel || (fromVersion != null ? `V${fromVersion}` : "Before");
  const to = toLabel || (toVersion != null ? `V${toVersion}` : "After");
  // One row per change — short Was / Now / one sentence Difference
  const tableRows = (Array.isArray(diff.changes) ? diff.changes : [])
    .filter((c) => Array.isArray(c.diffs) && c.diffs.length > 0)
    .map((c) => {
      const d = c.diffs[0];
      return {
        type: c.type,
        activity: c.activity || c.title || "Item",
        where: c.where || "",
        was: d.before && d.before !== "" ? d.before : "—",
        now: d.after && d.after !== "" ? d.after : "—",
        note: d.note || c.whatChanged || c.human || "Session was updated.",
      };
    });

  const td = {
    padding: "11px 14px",
    fontSize: 13,
    fontWeight: 600,
    color: C.text,
    borderBottom: `1px solid ${C.border}`,
    verticalAlign: "top",
    lineHeight: 1.45,
    whiteSpace: "normal",
    wordBreak: "keep-all",
    overflowWrap: "break-word",
    hyphens: "none",
  };

  return (
    <Card style={{ padding: 0 }} sx={{ p: "0 !important", overflow: "hidden", mb: 2 }}>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
          background: C.surface,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, ...wrapCell, minWidth: 0 }}>
          Difference · {from} → {to}
          <span style={{ fontSize: 13, fontWeight: 600, color: C.muted, marginLeft: 8 }}>
            {tableRows.length}
          </span>
        </div>
        <Btn small variant="ghost" onClick={onClose}>Close</Btn>
      </div>

      {!tableRows.length ? (
        <div style={{ padding: 16, color: C.muted, fontSize: 13, fontWeight: 600 }}>
          No differences between these versions.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, minWidth: 800 }}>
            <colgroup>
              <col style={{ width: "9%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "25%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={TABLE_HEAD}>Type</th>
                <th style={TABLE_HEAD}>Item</th>
                <th style={TABLE_HEAD}>Slot</th>
                <th style={TABLE_HEAD}>Before ({from})</th>
                <th style={TABLE_HEAD}>After ({to})</th>
                <th style={{ ...TABLE_HEAD, borderRight: "none" }}>Change</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r, i) => {
                const style = TYPE_STYLE[r.type] || TYPE_STYLE.modified;
                return (
                  <tr key={i} style={{ background: i % 2 ? C.surfaceAlt : C.surface }}>
                    <td style={td}>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "3px 8px",
                        borderRadius: 5,
                        background: style.bg,
                        color: style.color,
                        whiteSpace: "nowrap",
                      }}>
                        {style.label}
                      </span>
                    </td>
                    <td style={{ ...td, fontWeight: 800 }}>{r.activity}</td>
                    <td style={{ ...td, color: C.muted, fontSize: 12 }}>{r.where || "—"}</td>
                    <td style={{ ...td, color: r.was === "—" ? C.faint : C.error }}>{r.was}</td>
                    <td style={{ ...td, color: r.now === "—" ? C.faint : C.success, fontWeight: 700 }}>{r.now}</td>
                    <td style={{ ...td, fontWeight: 700, borderRight: "none" }}>{r.note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function VersionHistory({ onRestored }) {
  const { notify, confirm } = useAppNotify();
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberFilter, setMemberFilter] = useState("all");
  const [pickVersion, setPickVersion] = useState("");
  const [selected, setSelected] = useState([]);
  const [diff, setDiff] = useState(null);
  const [compareMeta, setCompareMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [error, setError] = useState("");
  const [viewingId, setViewingId] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");
  const [viewDoc, setViewDoc] = useState(null);
  const compareRef = useRef(null);
  const viewRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await getVersions();
      if (r.success) {
        setVersions(Array.isArray(r.versions) ? r.versions : []);
        setMeta(r.meta || null);
        setMembers(Array.isArray(r.members) ? r.members : []);
      } else {
        setError(r.error || "Could not load versions");
        setVersions([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Could not load versions");
      setVersions([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible =
    memberFilter === "all"
      ? versions
      : versions.filter((v) => (v.memberKey || "default") === memberFilter);

  const visibleMeta = (() => {
    if (memberFilter === "all") return meta;
    const list = visible;
    const latest = list[0];
    const latestV = latest?.displayVersion || latest?.memberVersion || 0;
    return {
      ...(meta || {}),
      totalVersions: list.length,
      latestVersion: latestV,
      chain:
        latestV > 0
          ? Array.from({ length: Math.min(latestV, 12) }, (_, i) => `V${i + 1}`).join(" → ") +
            (latestV > 12 ? " → …" : "")
          : "",
    };
  })();

  const versionLabel = (v) => {
    const n = v?.displayVersion || v?.memberVersion;
    const name = v?.memberName || "Solo";
    if (Number.isFinite(Number(n)) && Number(n) >= 1) return `${name} · V${n}`;
    return `${name} · V?`;
  };

  const closeView = () => {
    setViewingId(null);
    setViewDoc(null);
    setViewError("");
    setViewLoading(false);
  };

  const handleView = async (globalVersion) => {
    const row = versions.find((v) => v.version === globalVersion);
    setViewingId(globalVersion);
    setViewLoading(true);
    setViewError("");
    setViewDoc(null);
    setDiff(null);
    setCompareMeta(null);
    try {
      const res = await getVersion(globalVersion);
      if (res.success && res.versionDoc) {
        setViewDoc(res.versionDoc);
        setPickVersion(String(globalVersion));
        if (row?.memberKey) setMemberFilter(row.memberKey);
        notify({
          type: "success",
          title: "Schedule loaded",
          message: `Showing ${row ? versionLabel(row) : `Version ${globalVersion}`}`,
        });
        setTimeout(() => viewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
      } else {
        setViewError(res.error || "Could not load this version’s schedule");
        notify({ type: "error", title: "View failed", message: res.error || "Could not load schedule" });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Could not load schedule";
      setViewError(msg);
      notify({ type: "error", title: "View failed", message: msg });
    }
    setViewLoading(false);
  };

  const openViewInPlanner = async () => {
    const plan = viewDoc?.plan;
    if (!plan) return;
    const row = versions.find((v) => v.version === viewingId);
    const label = row ? versionLabel(row) : `Version ${viewingId}`;
    const normalized = Array.isArray(plan)
      ? { module: "daily", rows: plan }
      : { ...plan, module: plan?.module || "daily" };
    try {
      const raw = localStorage.getItem("ap_plans");
      const plans = raw ? JSON.parse(raw) : {};
      plans.daily = normalized;
      localStorage.setItem("ap_plans", JSON.stringify(plans));
      localStorage.setItem(
        "ap_version_preview",
        JSON.stringify({
          label,
          version: viewingId,
          memberKey: row?.memberKey || viewDoc?.memberKey,
          memberName: row?.memberName || viewDoc?.memberName,
          memberVersion: row?.displayVersion || row?.memberVersion || viewDoc?.memberVersion,
          at: Date.now(),
        })
      );
    } catch (_) {}
    try {
      if (localStorage.getItem("token") && normalized.rows?.length) {
        const { syncDailyPlan, setBasePlan } = await import("../api");
        await setBasePlan(normalized).catch(() => null);
        await syncDailyPlan(normalized).catch(() => null);
      }
    } catch (_) {}
    try {
      window.dispatchEvent(
        new CustomEvent("ap:plan-preview", {
          detail: {
            plan: normalized,
            label,
            version: viewingId,
            memberVersion: row?.displayVersion || row?.memberVersion || viewDoc?.memberVersion,
            memberKey: row?.memberKey || viewDoc?.memberKey,
            memberName: row?.memberName || viewDoc?.memberName,
          },
        })
      );
    } catch (_) {}
    const stats = summarizePlanRows(normalized.rows || []);
    notify({
      type: "info",
      title: "Opened in Planner",
      message: `${label} — exact snapshot (${stats.weeks} week(s), ${stats.days} day(s))`,
    });
    navigate("/planner?tab=plans");
  };

  const toggleSelect = (globalVersion) => {
    setDiff(null);
    setCompareMeta(null);
    if (selected.includes(globalVersion)) {
      setSelected(selected.filter((x) => x !== globalVersion));
      return;
    }
    if (selected.length < 2) setSelected([...selected, globalVersion]);
    else setSelected([selected[1], globalVersion]);
  };

  const handleCompare = async () => {
    if (selected.length !== 2) {
      notify({
        type: "warning",
        title: "Select versions",
        message: "Pick any 2 versions (same or different students), then click Compare.",
      });
      return;
    }
    const ra = versions.find((v) => v.version === selected[0]);
    const rb = versions.find((v) => v.version === selected[1]);
    const a = selected[0];
    const b = selected[1];
    setComparing(true);
    setDiff(null);
    try {
      const res = await compareVersions(a, b);
      if (res.success && res.diff) {
        setDiff(res.diff);
        setCompareMeta({
          from: res.previousDisplayVersion,
          to: res.newDisplayVersion,
          fromLabel: res.fromLabel || versionLabel(ra),
          toLabel: res.toLabel || versionLabel(rb),
          crossMember: Boolean(res.crossMember),
        });
        notify({
          type: "success",
          title: res.crossMember ? "Student schedules compared" : "Comparison ready",
          message: `${res.fromLabel || versionLabel(ra)} vs ${res.toLabel || versionLabel(rb)} — ${res.diff.totalChanges || 0} change(s)`,
        });
        setTimeout(() => compareRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
      } else {
        notify({ type: "error", title: "Compare failed", message: res.error || "Could not compare versions" });
      }
    } catch (err) {
      notify({
        type: "error",
        title: "Compare failed",
        message: err.response?.data?.error || err.message || "Could not compare versions",
      });
    }
    setComparing(false);
  };

  const handleRestore = async (versionNum) => {
    const row = versions.find((v) => v.version === versionNum);
    const personLabel = row ? versionLabel(row) : `Version ${versionNum}`;
    const ok = await confirm({
      title: `Rollback ${personLabel}?`,
      message: "Your current schedule will be replaced with this older version as a new latest for that student.",
      confirmLabel: "Rollback",
      cancelLabel: "Cancel",
      tone: "primary",
    });
    if (!ok) return;
    setRestoring(versionNum);
    try {
      const res = await restoreVersion(versionNum);
      if (res.success) {
        notify({
          type: "success",
          title: "Rollback complete",
          message: `${res.memberName || "Student"} restored as Version ${res.memberVersion || res.newVersion}`,
        });
        if (typeof onRestored === "function") onRestored(res.plan, res.newVersion);
        else {
          try {
            window.dispatchEvent(new CustomEvent("ap:plan-restored", {
              detail: { plan: res.plan, newVersion: res.newVersion },
            }));
          } catch (_) {}
        }
        try {
          const raw = localStorage.getItem("ap_plans");
          const plans = raw ? JSON.parse(raw) : {};
          if (res.plan) {
            plans.daily = res.plan;
            localStorage.setItem("ap_plans", JSON.stringify(plans));
          }
        } catch (_) {}
        await load();
      } else {
        notify({ type: "error", title: "Rollback failed", message: res.error || "Could not restore" });
      }
    } catch (err) {
      notify({
        type: "error",
        title: "Rollback failed",
        message: err.response?.data?.error || "Could not restore",
      });
    }
    setRestoring(null);
  };

  const handleProtect = async (versionNum, currentlyProtected) => {
    const next = !currentlyProtected;
    const ok = await confirm({
      title: next ? `Protect Version ${versionNum}?` : `Unprotect Version ${versionNum}?`,
      message: next
        ? "Faculty changes on this version will be locked from auto overwrite."
        : "This version can be changed again by regenerate or leave updates.",
      confirmLabel: next ? "Protect" : "Unprotect",
      cancelLabel: "Cancel",
      tone: "warning",
    });
    if (!ok) return;
    try {
      const res = await setVersionProtected(versionNum, next);
      if (res.success) {
        notify({
          type: "success",
          title: next ? "Version protected" : "Protection removed",
          message: next
            ? `V${versionNum} is locked against auto overwrite`
            : `V${versionNum} can be updated by regenerate / leave again`,
        });
        await load();
      } else {
        notify({ type: "error", title: "Update failed", message: res.error || "Could not update protection" });
      }
    } catch (err) {
      notify({
        type: "error",
        title: "Update failed",
        message: err.response?.data?.error || "Could not update protection",
      });
    }
  };

  const handleDelete = async (versionNum) => {
    const ok = await confirm({
      title: `Delete Version ${versionNum}?`,
      message: "This permanently removes the version from history. Your live schedule stays the same.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      tone: "error",
    });
    if (!ok) return;
    try {
      const res = await deleteVersion(versionNum);
      if (res.success) {
        notify({
          type: "success",
          title: "Version deleted",
          message: `V${versionNum} removed from history`,
        });
        setSelected((s) => s.filter((x) => x !== versionNum));
        setDiff(null);
        await load();
      } else {
        notify({ type: "error", title: "Delete failed", message: res.error || "Could not delete" });
      }
    } catch (err) {
      notify({
        type: "error",
        title: "Delete failed",
        message: err.response?.data?.error || "Could not delete",
      });
    }
  };

  return (
    <div style={{ maxWidth: 1440, margin: "0 auto", padding: "8px 4px 32px", fontFamily: "inherit" }}>
      <PageHeader
        title="Version Management"
        subtitle="Open any Version 1 / 2 / 3… schedule, compare two versions (same or different students), or rollback."
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Link
              to="/logs"
              title="Open Maintain Logs"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: LAYOUT.radiusSm || 10,
                border: `1.5px solid ${C.borderStrong}`,
                background: C.surface,
                color: C.text,
                fontSize: 13,
                fontWeight: 750,
                fontFamily: "inherit",
                textDecoration: "none",
                lineHeight: 1.2,
              }}
            >
              <i className="ti ti-clipboard-list" style={{ fontSize: 16, color: C.primary }} />
              Maintain Logs
            </Link>
            <Btn variant="ghost" onClick={load} disabled={loading || comparing} title="Refresh versions">
              <i
                className="ti ti-refresh"
                style={{
                  fontSize: 15,
                  marginRight: 6,
                  display: "inline-block",
                  animation: loading ? "ap-vh-spin 0.8s linear infinite" : "none",
                }}
              />
              {loading ? "Refreshing…" : "Refresh"}
          </Btn>
          </div>
        }
      />

      {/* Student + version jump — scales to many users */}
      <Card style={{ marginBottom: 14 }} sx={{ p: 2 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 260px", minWidth: 220, maxWidth: 400 }}>
            <FancySelect
              tone="blue"
              label="Student"
              hint={
                members.length > 1
                  ? `${members.length} students — each has their own Version 1, 2, 3…`
                  : "Choose whose schedule to open"
              }
              icon="ti-users"
              value={memberFilter}
              onChange={(e) => {
                setMemberFilter(e.target.value || "all");
                setPickVersion("");
                setSelected([]);
                setDiff(null);
                setCompareMeta(null);
                closeView();
              }}
            >
              <option value="all">
                All students{members.length ? ` (${members.length})` : ""}
              </option>
              {members.map((m) => (
                <option key={m.memberKey} value={m.memberKey}>
                  {m.memberName || m.memberKey}
                  {m.latestMemberVersion ? ` · latest V${m.latestMemberVersion}` : ""}
                  {m.totalVersions ? ` · ${m.totalVersions} saved` : ""}
                </option>
              ))}
            </FancySelect>
          </div>
          <div style={{ flex: "1 1 220px", minWidth: 200, maxWidth: 320 }}>
            <FancySelect
              tone="blue"
              label="Their version"
              hint={
                memberFilter === "all"
                  ? "Select a student first, then pick V1 / V2…"
                  : visible.length
                    ? `${visible.length} version(s) for this student`
                    : "No versions for this student yet"
              }
              icon="ti-versions"
              value={pickVersion}
              disabled={memberFilter === "all" || visible.length === 0}
              onChange={(e) => setPickVersion(e.target.value || "")}
            >
              <option value="">
                {memberFilter === "all" ? "Select student first" : "Pick Version…"}
              </option>
              {memberFilter !== "all" &&
                visible.map((v) => (
                  <option key={v.version} value={String(v.version)}>
                    V{v.displayVersion || v.memberVersion || "?"}
                    {v.isLatestForMember ? " · Latest" : ""}
                    {v.changeType ? ` · ${friendlyChangeType(v.changeType)}` : ""}
                    {` · ${formatWhen(v.createdAt)}`}
                  </option>
                ))}
            </FancySelect>
          </div>
          <Btn
            onClick={() => {
              const id = Number(pickVersion);
              if (!id) {
                notify({
                  type: "warning",
                  title: "Pick a version",
                  message: "Choose a student, then their Version number, then View schedule.",
                });
                return;
              }
              handleView(id);
            }}
            disabled={memberFilter === "all" || !pickVersion || viewLoading}
          >
            {viewLoading ? "Opening…" : "View schedule"}
          </Btn>
          {memberFilter !== "all" && (
            <Btn
              small
              variant="ghost"
              onClick={() => {
                setMemberFilter("all");
                setPickVersion("");
                setSelected([]);
                setDiff(null);
                setCompareMeta(null);
                closeView();
              }}
            >
              Clear filter
            </Btn>
          )}
        </div>
      </Card>

      <div ref={viewRef}>
        {(viewingId || viewLoading || viewError || viewDoc) && (
          <VersionSchedulePreview
            label={
              viewDoc
                ? `${viewDoc.memberName || "Student"} · V${viewDoc.memberVersion || "—"}`
                : versions.find((v) => v.version === viewingId)
                  ? versionLabel(versions.find((v) => v.version === viewingId))
                  : `Version ${viewingId}`
            }
            plan={viewDoc?.plan}
            loading={viewLoading}
            error={viewError}
            onClose={closeView}
            onOpenInPlanner={openViewInPlanner}
          />
        )}
      </div>

      {/* Selection helper — keeps compare obvious */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 14,
          padding: "12px 14px",
          borderRadius: LAYOUT.radius,
          border: `1px solid ${selected.length === 2 ? `${C.primary}55` : C.border}`,
          background: selected.length === 2 ? C.primarySoft : C.surface,
          boxShadow: "0 1px 2px rgba(10,14,23,0.04)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>
            {selected.length === 0 && "Click rows to select versions for compare"}
            {selected.length === 1 && (() => {
              const row = versions.find((v) => v.version === selected[0]);
              return `Selected ${row ? versionLabel(row) : `V${selected[0]}`} — pick one more`;
            })()}
            {selected.length === 2 && (() => {
              const a = versions.find((v) => v.version === selected[0]);
              const b = versions.find((v) => v.version === selected[1]);
              return `Ready to compare ${a ? versionLabel(a) : `V${selected[0]}`} and ${b ? versionLabel(b) : `V${selected[1]}`}`;
            })()}
          </div>
          <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginTop: 2 }}>
            {visibleMeta?.totalVersions
              ? memberFilter === "all" && (visibleMeta.memberCount || members.length) > 1
                ? `${visibleMeta.totalVersions} versions · ${visibleMeta.memberCount || members.length} students (each starts at V1)`
                : `Latest V${visibleMeta.latestVersion} · ${visibleMeta.totalVersions || 0} saved${visibleMeta.chain ? ` · ${visibleMeta.chain}` : ""}`
              : "Generate a schedule to create Version 1 for that student"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {selected.length > 0 && (
            <Btn
              small
              variant="ghost"
              onClick={() => { setSelected([]); setDiff(null); setCompareMeta(null); }}
            >
              Clear
            </Btn>
          )}
          <Btn small onClick={handleCompare} disabled={comparing || selected.length !== 2}>
            {comparing ? "Comparing…" : "Compare"}
          </Btn>
        </div>
      </div>

      {error && (
        <div style={{
          padding: "10px 14px",
          borderRadius: LAYOUT.radiusSm,
          marginBottom: 14,
          fontSize: 13,
          fontWeight: 600,
          background: C.errorBg,
          color: C.error,
          border: `1px solid ${C.error}33`,
        }}>
          {error}
        </div>
      )}

      <div ref={compareRef}>
        {diff && (
          <CompareView
            diff={diff}
            fromLabel={compareMeta?.fromLabel}
            toLabel={compareMeta?.toLabel}
            fromVersion={compareMeta?.from}
            toVersion={compareMeta?.to}
            onClose={() => { setDiff(null); setCompareMeta(null); }}
          />
        )}
      </div>

      <Card style={{ padding: 0 }} sx={{ p: "0 !important", overflow: "hidden" }}>
        {loading && versions.length === 0 ? (
          <p style={{ padding: 20, color: C.muted, fontSize: 13, fontWeight: 600 }}>Loading versions…</p>
        ) : visible.length === 0 ? (
          <p style={{ padding: 20, color: C.muted, fontSize: 13, fontWeight: 600 }}>
            No versions yet. Generate a schedule to create Version 1.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                minWidth: 980,
                tableLayout: "fixed",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: 12.5,
                fontFamily: "inherit",
              }}
            >
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={TABLE_HEAD} aria-label="Select" />
                  <th style={TABLE_HEAD}>Student</th>
                  <th style={TABLE_HEAD}>Version</th>
                  <th style={TABLE_HEAD}>Type</th>
                  <th style={TABLE_HEAD}>By</th>
                  <th style={TABLE_HEAD}>When</th>
                  <th style={{ ...TABLE_HEAD, borderRight: "none" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((v, i) => {
                  const selectedRow = selected.includes(v.version);
                  const email = byEmail(v);
                  const student = v.memberName || "Solo";
            return (
                    <tr
                key={v.version}
                onClick={() => toggleSelect(v.version)}
                style={{
                  cursor: "pointer",
                        background: selectedRow ? C.primarySoft : i % 2 ? C.surfaceAlt : C.surface,
                        borderBottom: `1px solid ${C.border}`,
                }}
              >
                      <td style={TD}>
                <div style={{
                          width: 18, height: 18, borderRadius: 4,
                          border: `2px solid ${selectedRow ? C.primary : C.borderStrong}`,
                          background: selectedRow ? C.primary : "transparent",
                          color: "#fff", fontSize: 11, fontWeight: 800,
                          display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                          {selectedRow ? "✓" : ""}
                </div>
                      </td>
                      <td style={{ ...TD, ...wrapCell }} title={student}>
                        <div style={{ fontWeight: 800, fontSize: 13, color: C.text, ...wrapCell }}>
                          {student}
                </div>
                      </td>
                      <td style={{ ...TD, whiteSpace: "nowrap" }}>
                        <div style={{ fontWeight: 800, fontSize: 15, color: C.primary, letterSpacing: "-0.02em" }}>
                          V{v.displayVersion || v.memberVersion || "—"}
                </div>
                      </td>
                      <td style={TD}>
                        <TypeChips v={v} />
                      </td>
                      <td style={{ ...TD, ...wrapCell }}>
                        {email ? (
                          <a
                            href={`mailto:${email}`}
                            title={email}
                            onClick={(e) => e.stopPropagation()}
              style={{
                              color: C.primary,
                              fontWeight: 700,
                              fontSize: 12.5,
                              textDecoration: "underline",
                              textUnderlineOffset: 2,
                              whiteSpace: "normal",
                              wordBreak: "normal",
                              overflowWrap: "anywhere",
                              lineHeight: 1.35,
                            }}
                          >
                            {email}
                          </a>
                        ) : (
                          <span style={{ fontWeight: 600, color: C.faint, fontSize: 12.5 }}>—</span>
                        )}
                      </td>
                      <td style={{ ...TD, color: C.muted, whiteSpace: "nowrap", fontWeight: 600, fontSize: 12 }}>
                        {formatWhen(v.createdAt)}
                      </td>
                      <td style={{ ...TD, borderRight: "none", overflow: "visible" }} onClick={(e) => e.stopPropagation()}>
                        <VersionActions
                          versionNum={v.version}
                          isLatest={Boolean(v.isLatestForMember)}
                          isProtected={Boolean(v.protected)}
                          restoring={restoring === v.version}
                          viewing={viewLoading && viewingId === v.version}
                          onView={handleView}
                          onRollback={handleRestore}
                          onProtect={handleProtect}
                          onDelete={handleDelete}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      <style>{`
        @keyframes ap-vh-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
